import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@exhousing/design-system";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Rocket, XCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { apiClient } from "@/api/client";
import { usePublishReadiness } from "@/hooks/api/useProperties";

/** Journey 1.3: the pre-publish checklist with the explicit publish action. */
export function PublishReadinessCard({ propertyId }: { propertyId: string }) {
  const qc = useQueryClient();
  const { data } = usePublishReadiness(propertyId);
  const [confirming, setConfirming] = useState(false);
  const publish = useMutation({
    mutationFn: async () => {
      const { data: result, error } = await apiClient.POST(
        "/api/v1/properties/{property_id}/publish",
        { params: { path: { property_id: propertyId } } },
      );
      if (error || !result) throw new Error("Could not publish");
      return result;
    },
    onSuccess: () => {
      toast.success("Property published");
      qc.invalidateQueries({ queryKey: ["properties"] });
    },
    onError: (err) => toast.error(err.message),
  });

  if (!data || data.is_published) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Rocket className="size-4" /> Ready to publish?
        </CardTitle>
        <Button
          size="sm"
          disabled={!data.ready || publish.isPending}
          onClick={() => (confirming ? publish.mutate() : setConfirming(true))}
        >
          <Rocket className="size-4" />
          {confirming ? "Yes, publish — candidates can apply" : "Publish now"}
        </Button>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {data.checks.map((check) => (
            <li key={check.key} className="flex items-start gap-2.5 text-sm">
              {check.ok ? (
                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />
              ) : check.blocking ? (
                <XCircle className="mt-0.5 size-4 shrink-0 text-red-600" />
              ) : (
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-500" />
              )}
              <span>
                <span className={check.ok ? "" : "font-medium"}>{check.label}</span>
                {!check.ok && check.detail && (
                  <span className="block text-xs text-muted-foreground">
                    {check.detail}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
        {!data.ready && (
          <p className="mt-3 text-xs text-muted-foreground">
            Resolve the red blockers to enable publishing; amber items are
            recommendations.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
